import { useState } from 'react';
import { useSiteData } from '@/contexts/SiteDataContext';
import type { SocialLink } from '@/contexts/SiteDataContext';

export default function SocialLinksSection() {
  const { data, updateSocialLinks } = useSiteData();
  const [links, setLinks] = useState<SocialLink[]>([...data.socialLinks]);
  const [saved, setSaved] = useState(false);

  const handleSave = () => { updateSocialLinks(links); setSaved(true); setTimeout(() => setSaved(false), 2500); };
  const update = (i: number, field: keyof SocialLink, val: string) => setLinks(links.map((l, idx) => idx === i ? { ...l, [field]: val } : l));
  const add = () => setLinks([...links, { name: 'New Platform', icon: 'ri-links-line', url: '#' }]);
  const remove = (i: number) => setLinks(links.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-foreground-900 mb-1">Social Media Links</h2>
          <p className="text-foreground-500 text-sm">Edit social media URLs shown in the footer and navigation.</p>
        </div>
        <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary-500 text-white rounded-xl text-sm font-semibold hover:bg-secondary-600 transition-colors whitespace-nowrap">
          <i className="ri-add-line"></i> Add Platform
        </button>
      </div>

      <div className="space-y-3">
        {links.map((link, i) => (
          <div key={i} className="flex items-center gap-3 p-4 border border-background-200 rounded-2xl bg-background-50">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
              <i className={`${link.icon} text-primary-600 text-lg`}></i>
            </div>
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input
                type="text"
                value={link.name}
                onChange={(e) => update(i, 'name', e.target.value)}
                placeholder="Platform name"
                className="px-3 py-2 rounded-xl border border-background-200 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-300"
              />
              <input
                type="text"
                value={link.icon}
                onChange={(e) => update(i, 'icon', e.target.value)}
                placeholder="ri-facebook-fill"
                className="px-3 py-2 rounded-xl border border-background-200 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-300"
              />
              <input
                type="url"
                value={link.url}
                onChange={(e) => update(i, 'url', e.target.value)}
                placeholder="https://facebook.com/..."
                className="px-3 py-2 rounded-xl border border-background-200 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-300"
              />
            </div>
            <button onClick={() => remove(i)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-rose-100 hover:bg-rose-200 text-rose-600 transition-colors flex-shrink-0 cursor-pointer">
              <i className="ri-delete-bin-line text-sm"></i>
            </button>
          </div>
        ))}
      </div>

      <div className="p-4 bg-background-100 rounded-xl text-xs text-foreground-500 space-y-1">
        <p className="font-semibold text-foreground-700 mb-2">Common Remix Icon classes:</p>
        <div className="flex flex-wrap gap-3">
          {[
            { icon: 'ri-facebook-fill', label: 'Facebook' },
            { icon: 'ri-instagram-line', label: 'Instagram' },
            { icon: 'ri-twitter-x-fill', label: 'X/Twitter' },
            { icon: 'ri-youtube-fill', label: 'YouTube' },
            { icon: 'ri-linkedin-fill', label: 'LinkedIn' },
            { icon: 'ri-whatsapp-line', label: 'WhatsApp' },
          ].map(({ icon, label }) => (
            <span key={icon} className="flex items-center gap-1.5 px-2 py-1 bg-white rounded-lg border border-background-200">
              <i className={`${icon} text-primary-500`}></i>
              <code className="text-[10px]">{icon}</code>
              <span>= {label}</span>
            </span>
          ))}
        </div>
      </div>

      <button onClick={handleSave} className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap ${saved ? 'bg-emerald-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'}`}>
        <i className={saved ? 'ri-check-line' : 'ri-save-line'}></i>
        {saved ? 'Saved!' : 'Save Social Links'}
      </button>
    </div>
  );
}